// The ONE translation between Otto's wildcard operators and the app's. Two vocabularies exist and nothing
// in the type system connects them:
//
//   Otto / filter-ai   `WildcardOperator` (filter-ai/app/capabilities/dashboard_filters/intent.py)
//                      contains, not_contains, equals, not_equals, starts_with, ends_with,
//                      gt, gte, lt, lte, between, is_empty, is_not_empty
//   Every filter panel  src/utils/wildCardFilterUtils.js (numberFilterKeys / stringFilterKeys /
//                      dateFilterKeys) — the SAME file backs the landing dashboard's filter window and the
//                      asset one, and Ruby's `Filterable#filter` turns each token into a scope by name
//                      (`@query.send(scope, field, key)`), so an unknown token has no method behind it.
//
// Only `contains` is spelled the same on both sides. That single coincidence is why the gap survived: text
// wildcards worked, so nothing looked broken until someone asked for a threshold (FTO-91 — "assets that have
// exceeded useful service life" wrote {key:"0", filters:["lt"]}, which the panel renders as a value with no
// operator and the API cannot execute).
//
// Every surface that writes a panel wildcard entry from an interpreted intent MUST come through here — the
// landing dashboard (capabilities/dashboardFilters/applyMapping.js) and both asset mounts
// (assets/ottoAssetFilters.js). The `table_view` column filters are the exception BY CONSTRUCTION: they never
// see an interpret id, because tableCatalog.js parses phrases straight into the panel's own vocabulary. The
// xrepo-wildcard-operators gate enforces all of that.
import { parseMagnitudeNumber } from "./parseMagnitude";
import { getWildcardFilterOperatorLabel } from "../../../utils/wildCardFilterUtils";

// Assistant operator id -> panel wildcard operator token. `equals` is deliberately absent: it resolves by
// field type below (text columns want a case-insensitive string compare, numeric ones a numeric one).
export const OPERATOR_MAP = {
    gt: "greater_than",
    gte: "greater_than_or_equal",
    lt: "less_than",
    lte: "less_than_or_equal",
    not_equals: "unequals",
    contains: "contains",
    not_contains: "exclude",
    starts_with: "begins_with",
    ends_with: "ends_with",
    between: "in_between",
    is_empty: "null",
    is_not_empty: "not_null"
};

/**
 * The panel token one assistant operator becomes on a field of this type. `equals` is the only operator
 * whose token depends on the column: text columns want a case-insensitive string compare
 * (`string_equals`), numeric ones a numeric compare (`equals`).
 *
 * @returns the token, or undefined when this build has no mapping for the operator.
 */
export const panelOperatorToken = (operator, fieldType) =>
    operator === "equals" ? (fieldType === "text" ? "string_equals" : "equals") : OPERATOR_MAP[operator];

/**
 * The filter window's OWN wording for an assistant operator ("gt" on a number -> "Greater Than"), so Otto
 * names an operator the way the panel the user is about to see names it.
 *
 * Not every assistant operator has panel wording: the date list is presets + "In between" (no </>), and the
 * number list has no range option at all. `getWildcardFilterOperatorLabel` echoes the raw token back when
 * its list has no entry, which would surface "less_than" in the UI — so an un-worded operator falls back to
 * the caller's own label instead.
 */
export const panelOperatorLabel = (operator, fieldType, fallback) => {
    const token = panelOperatorToken(operator, fieldType);
    if (!token) return fallback;
    const label = getWildcardFilterOperatorLabel(token, fieldType);
    return !label || label === token ? fallback : label;
};

/**
 * Translate one resolved wildcard into the entry every filter panel stores: `{ key, filters: [operator] }`.
 *
 * @param operator   the assistant's operator id (interpret schema)
 * @param value      the assistant's value (may be a magnitude shorthand like "50k")
 * @param valueRange the assistant's `value_range` ({from_value,to_value}), for `between`
 * @param fieldType  the field config's type — "text" means no magnitude expansion and a string equals
 * @returns `{ entry, operator }` on success, or `{ dropped: reason }` — never a half-translated entry.
 *
 * Callers own the KEY the entry is filed under (the dashboard uses `paramKey`, the asset panel `searchKey`)
 * and their own diagnostics; only the value shape is shared, because only the value shape is identical.
 */
export const toPanelWildcardEntry = ({ operator, value, valueRange, fieldType } = {}) => {
    const op = panelOperatorToken(operator, fieldType);
    if (!op) {
        // An operator the map doesn't know is a NEW backend token this build predates. The old dashboard
        // fallback substituted contains/equals — data looked filtered but was filtered WRONG — and the asset
        // adapter wrote the raw id, which is worse (no operator at all). Dropping is the honest answer: the
        // caller reports it and the apply span turns red.
        return { dropped: "unknown operator" };
    }
    // Numeric fields: expand magnitude shorthand the interpreter (or an inline-edited pill) may have left
    // as-is — "10k" -> 10000, "1m" -> 1000000. Text fields keep their value verbatim (never a magnitude).
    // A value that carries no number falls through unchanged, so bad input degrades to today's behavior.
    const numeric = fieldType !== "text";
    const expand = v => {
        if (!numeric || v == null || v === "") return v;
        const n = parseMagnitudeNumber(v);
        return n == null ? v : n;
    };
    const key =
        operator === "between" && valueRange
            ? { from: expand(valueRange.from_value), to: expand(valueRange.to_value) }
            : value != null
            ? String(expand(value))
            : "";
    return { entry: { key, filters: [op] }, operator: op };
};
